import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Line3,
  type Mesh,
  Ray,
  Vector3,
} from 'three';
import { MeshBVH } from 'three-mesh-bvh';

export interface CapsuleContact {
  /** Push-out direction (unit), from level surface toward the capsule. */
  normal: Vector3;
  /** Penetration depth along the normal. */
  depth: number;
}

/**
 * Static level collision: world-space triangles of all collidable meshes baked
 * into a single BVH (three-mesh-bvh — adopted via the Phase 1 spike, ADR 0004).
 * Queries are capsule push-out tests used by the kinematic PlayerController;
 * geometry is immutable after construction.
 */
export class LevelCollider {
  private readonly bvh: MeshBVH;

  // Scratch objects — queries allocate nothing per call except returned contacts.
  private readonly segment = new Line3();
  private readonly aabb = new Box3();
  private readonly triPoint = new Vector3();
  private readonly capsulePoint = new Vector3();
  private readonly faceNormal = new Vector3();
  private readonly ray = new Ray();

  constructor(meshes: Mesh[]) {
    const merged = mergeWorldPositions(meshes);
    this.bvh = new MeshBVH(merged);
  }

  /**
   * Finds all surfaces the capsule penetrates. The capsule stands on `feet`
   * (bottom point) with the given total height and radius, axis-aligned to +Y.
   * Returns contacts sorted by depth (deepest first).
   */
  capsuleContacts(feet: Vector3, radius: number, height: number): CapsuleContact[] {
    // Internal segment of the capsule (sphere centers at both ends).
    this.segment.start.set(feet.x, feet.y + radius, feet.z);
    this.segment.end.set(feet.x, feet.y + height - radius, feet.z);
    this.aabb.makeEmpty();
    this.aabb.expandByPoint(this.segment.start);
    this.aabb.expandByPoint(this.segment.end);
    this.aabb.min.addScalar(-radius);
    this.aabb.max.addScalar(radius);

    const contacts: CapsuleContact[] = [];
    this.bvh.shapecast({
      intersectsBounds: (box) => box.intersectsBox(this.aabb),
      intersectsTriangle: (tri) => {
        const distance = tri.closestPointToSegment(this.segment, this.triPoint, this.capsulePoint);
        if (distance < radius) {
          tri.getNormal(this.faceNormal);
          const direction = this.capsulePoint.clone().sub(this.triPoint);
          if (direction.lengthSq() < 1e-12) {
            // Segment point exactly on the surface.
            contacts.push({ normal: this.faceNormal.clone(), depth: radius });
          } else if (direction.dot(this.faceNormal) >= 0) {
            // Segment is on the front side: push along the separation vector.
            contacts.push({ normal: direction.divideScalar(distance), depth: radius - distance });
          } else {
            // Segment has crossed the surface: the separation vector points
            // into the solid. Push back out along the face normal far enough
            // to clear the surface, or fast movers wedge inside ramps.
            contacts.push({ normal: this.faceNormal.clone(), depth: radius + distance });
          }
        }
        return false; // keep collecting
      },
    });

    return contacts.sort((a, b) => b.depth - a.depth);
  }

  /**
   * Distance along `direction` (unit) to the first level surface within
   * `maxDistance`, or null. Double-sided so line-of-sight/projectile queries
   * (spec 0025) hit ramp backfaces too.
   */
  raycastFirst(origin: Vector3, direction: Vector3, maxDistance: number): number | null {
    this.ray.origin.copy(origin);
    this.ray.direction.copy(direction);
    const hit = this.bvh.raycastFirst(this.ray, DoubleSide, 0, maxDistance);
    return hit ? hit.distance : null;
  }
}

/**
 * Bakes meshes into one position-only, world-space geometry. Dropping indices
 * and extra attributes keeps merging trivial and is all collision needs.
 */
function mergeWorldPositions(meshes: Mesh[]): BufferGeometry {
  const arrays: Float32Array[] = [];
  let total = 0;
  const v = new Vector3();

  for (const mesh of meshes) {
    mesh.updateWorldMatrix(true, false);
    const source = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const position = source.getAttribute('position');
    const out = new Float32Array(position.count * 3);
    for (let i = 0; i < position.count; i++) {
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      out[i * 3] = v.x;
      out[i * 3 + 1] = v.y;
      out[i * 3 + 2] = v.z;
    }
    arrays.push(out);
    total += out.length;
  }

  const positions = new Float32Array(total);
  let offset = 0;
  for (const a of arrays) {
    positions.set(a, offset);
    offset += a.length;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  return geometry;
}
